import type { BankTransactionView, TitleView } from "@cx/contracts";
import type {
  AccountKind,
  CategoryKind,
  DreGroup,
  Occurrence,
  SefazPaymentType,
  TitleSide,
  TitleStatus,
} from "@cx/domain";
import type { Tone } from "@cx/ui";

/** Textos por lado: contas a receber (R) e a pagar (P). */
export const SIDE = {
  R: {
    title: "Contas a receber",
    noun: "conta a receber",
    path: "/financas/contas-a-receber",
    detail: "/financas/contas-a-receber/$id",
    api: "/receivables",
    module: "receivables",
    contact: "Cliente",
    settle: "Receber",
    settled: "Recebida",
    bulk: "Receber selecionadas",
  },
  P: {
    title: "Contas a pagar",
    noun: "conta a pagar",
    path: "/financas/contas-a-pagar",
    detail: "/financas/contas-a-pagar/$id",
    api: "/payables",
    module: "payables",
    contact: "Fornecedor",
    settle: "Pagar",
    settled: "Paga",
    bulk: "Pagar selecionadas",
  },
} as const satisfies Record<TitleSide, Record<string, string>>;

/** Situação mostrada: atrasada vence sobre "em aberto"; quitada usa "Recebida"/"Paga". */
export function titleBadge(t: Pick<TitleView, "status" | "overdue" | "side">): { label: string; tone: Tone } {
  if (t.status === "canceled") return { label: "Cancelada", tone: "secondary" };
  if (t.status === "paid") return { label: SIDE[t.side].settled, tone: "success" };
  if (t.overdue) return { label: "Atrasada", tone: "danger" };
  if (t.status === "partial") return { label: "Parcial", tone: "info" };
  return { label: "Em aberto", tone: "warning" };
}

export const TITLE_STATUS_LABEL: Record<TitleStatus, string> = {
  open: "Em aberto",
  partial: "Parcial",
  paid: "Quitada",
  canceled: "Cancelada",
};

export const OCCURRENCE_LABEL: Record<Occurrence, string> = {
  single: "Única",
  installment: "Parcelada",
  weekly: "Semanal",
  biweekly: "Quinzenal",
  monthly: "Mensal",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

export const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
  cash: "Caixa",
  bank: "Conta bancária",
  card_acquirer: "Adquirente de cartão",
  wallet: "Carteira digital",
};

export const CATEGORY_KIND_LABEL: Record<CategoryKind, string> = { revenue: "Receita", expense: "Despesa" };

export const DRE_GROUP_LABEL: Record<DreGroup, string> = {
  gross_revenue: "Receita bruta",
  deductions: "Deduções da receita",
  cogs: "Custo das mercadorias/serviços",
  opex: "Despesas operacionais",
  financial: "Resultado financeiro",
  taxes: "Impostos sobre o lucro",
  other: "Outros",
};

export const SEFAZ_LABEL: Record<SefazPaymentType, string> = {
  "01": "Dinheiro",
  "02": "Cheque",
  "03": "Cartão de crédito",
  "04": "Cartão de débito",
  "05": "Crédito loja",
  "10": "Vale alimentação",
  "11": "Vale refeição",
  "12": "Vale presente",
  "13": "Vale combustível",
  "14": "Duplicata mercantil",
  "15": "Boleto bancário",
  "16": "Depósito bancário",
  "17": "Pix",
  "90": "Sem pagamento",
  "99": "Outros",
};

/** Histórico do lançamento no extrato. */
export function txLabel(
  t: Pick<BankTransactionView, "kind" | "reversalOfId" | "description" | "amountCents">,
) {
  const base = t.reversalOfId
    ? "Estorno"
    : t.kind === "transfer"
      ? `Transferência (${t.amountCents >= 0 ? "entrada" : "saída"})`
      : t.kind === "settlement"
        ? "Baixa de conta"
        : t.amountCents >= 0
          ? "Entrada"
          : "Saída";
  return t.description ? `${base} · ${t.description}` : base;
}

/** "Parcela 2/3" ou null. */
export const installmentLabel = (t: Pick<TitleView, "installmentNumber" | "installmentTotal">) =>
  t.installmentNumber && t.installmentTotal ? `${t.installmentNumber}/${t.installmentTotal}` : null;

/** Resumo do lote: "2 baixadas, 1 com erro". */
export function bulkSettleSummary(r: { okCount: number; failCount: number }) {
  const ok = `${r.okCount} ${r.okCount === 1 ? "baixada" : "baixadas"}`;
  return r.failCount ? `${ok}, ${r.failCount} com erro` : ok;
}
